// Trusler: stormskyen med øjne, der følger et synligt spor, gravkoen og ankerbåden.
// Varslet er altid tydeligt: et orange bånd, en advarselstrekant og en nedtælling i sekunder.

import { BitmapText, Container, Graphics, Sprite } from 'pixi.js';
import type { Threat } from '../sim';
import { TICK_HZ } from '../sim';
import { lineMid } from '../sim/grid';
import { cardOpen, isCard } from '../sim/threats';
import type { MapObject } from './cities-layer';
import type { Piece } from './declutter';
import { NUM_FONT } from './fx';
import { bez } from './geo';
import type { P } from './geo';
import { C } from './palette';
import { drawCardThreat, m2Texture } from './threats-m2';
import { drawLongThreat, isLongThreat, m5Texture } from './threats-m5';
import type { View } from './view';

interface ThreatView {
  id: string;
  body: Sprite;
  marker: Sprite;
  count: BitmapText;
  rain: Sprite[];
  /** Hvor figuren står på skærmen (til tap). */
  at: P;
  flee: number;
  fleeFrom: P | null;
  seed: number;
  /** Jaget væk lokalt (tappet), før sim'en har bekræftet det. */
  shooed: boolean;
  /** Retning (radianer) figuren kommer fra og flygter mod; ankerbåden kun over vand. */
  ang: number | null;
  /** Hvornår et kort blev besvaret (ms), til figurens hop. */
  popAt: number;
  /** Hvor på ledningen (0..1) figuren sidder: første punkt på land fra midten og ud. */
  perchT: number | null;
  kind: Threat['kind'];
  /** Spor: telefonkort: forskydningen væk fra huse, navne og andre figurer (declutter.ts), og målet. */
  nudge: P;
  placed?: boolean;
  target: P;
  /** Hvor meget en sky er tonet ned over et bynavn (0..1). */
  fade: number;
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619) >>> 0;
  return h;
}

export class ThreatsLayer {
  /** Båndet ligger under noderne, figurerne over. */
  readonly band = new Graphics();
  readonly c = new Container();
  private views = new Map<string, ThreatView>();

  /** Skærmrektangler for varselsmarkører og nedtællinger, så bynavne kan gå uden om dem. */
  rects(): Array<{ x: number; y: number; w: number; h: number }> {
    const out: Array<{ x: number; y: number; w: number; h: number }> = [];
    for (const t of this.views.values()) {
      for (const o of [t.marker, t.count]) {
        if (!o.visible || o.destroyed) continue;
        const b = o.getBounds();
        if (b.width > 0) out.push({ x: b.x, y: b.y, w: b.width, h: b.height });
      }
    }
    return out;
  }

  /**
   * Testkrog (scripts/maptest.mjs): figurer, trekanter og nedtællinger med ejer. Skyerne tælles ikke
   * (de er gennemsigtige og driver over kortet); en figur, der stadig løber ind, står ikke stille.
   */
  objectBoxes(state: { threats: readonly Threat[] }): MapObject[] {
    const out: MapObject[] = [];
    const byId = new Map(state.threats.map((t) => [t.id, t]));
    for (const tv of this.views.values()) {
      const t = byId.get(tv.id);
      if (!t) continue;
      const cloud = t.kind === 'storm' || t.kind === 'duo';
      const fixed = !cloud && !t.shooed && !tv.shooed && (isCard(t.kind) || isLongThreat(t.kind) || t.phase !== 'warning');
      for (const [type, o] of [['threat', cloud ? null : tv.body], ['marker', tv.marker], ['count', tv.count]] as const) {
        if (!o || !o.visible || o.destroyed || o.alpha < 0.3) continue;
        const b = o.getBounds();
        if (b.width > 0.5 && b.height > 0.5) out.push({ owner: `threat:${tv.id}`, type, box: { x: b.x, y: b.y, w: b.width, h: b.height }, fixed });
      }
    }
    return out;
  }

  /**
   * Figurerne, der står stille ved deres ting (declutter.ts): skyer driver og tæller ikke, og en
   * figur på vej ind eller ud skubbes ikke. Delene måles med den forskydning, der er tegnet nu.
   */
  pieces(state: { threats: readonly Threat[] }): Array<{ piece: Piece; view: { nudge: P; target: P; placed?: boolean }; parts: Container[] }> {
    const out: Array<{ piece: Piece; view: { nudge: P; target: P; placed?: boolean }; parts: Container[] }> = [];
    const byId = new Map(state.threats.map((t) => [t.id, t]));
    for (const tv of this.views.values()) {
      const t = byId.get(tv.id);
      if (!t || t.kind === 'storm' || t.kind === 'duo') continue;
      const still = !t.shooed && !tv.shooed && (isCard(t.kind) || isLongThreat(t.kind) || t.phase !== 'warning');
      if (!still) continue;
      const boxes = [];
      for (const o of [tv.body, tv.marker, tv.count]) {
        if (!o.visible || o.destroyed || o.alpha < 0.3) continue;
        const b = o.getBounds();
        if (b.width > 0.5 && b.height > 0.5) boxes.push({ x: b.x, y: b.y, w: b.width, h: b.height });
      }
      if (boxes.length) out.push({ piece: { owner: `threat:${tv.id}`, boxes, nudge: tv.nudge, target: tv.target }, view: tv, parts: [tv.body, tv.marker, tv.count] });
    }
    return out;
  }

  /**
   * Spor: telefonkort: en sky eller en front, der driver hen over et bynavn, bliver næsten gennemsigtig
   * der, så navnet kan læses. Skyens egen alfa sættes forfra hvert billede (storm, duo).
   */
  fadeOver(labels: readonly { x: number; y: number; w: number; h: number }[], state: { threats: readonly Threat[] }): void {
    const byId = new Map(state.threats.map((t) => [t.id, t]));
    for (const tv of this.views.values()) {
      const t = byId.get(tv.id);
      if (!t || (t.kind !== 'storm' && t.kind !== 'duo') || !tv.body.visible) continue;
      const b = tv.body.getBounds();
      // Skyens tegning fylder ikke hele kassen: kernen tæller.
      const core = { x: b.x + b.width * 0.15, y: b.y + b.height * 0.15, w: b.width * 0.7, h: b.height * 0.7 };
      const over = labels.some((l) => Math.min(l.x + l.w, core.x + core.w) - Math.max(l.x, core.x) > 2 && Math.min(l.y + l.h, core.y + core.h) - Math.max(l.y, core.y) > 2);
      tv.fade = Math.max(0, Math.min(1, tv.fade + (over ? 1 : -1) * 0.12));
      tv.body.alpha *= 1 - tv.fade * 0.6;
    }
  }

  reset(): void {
    for (const t of this.views.values()) this.kill(t);
    this.views.clear();
    this.band.clear();
  }

  private kill(t: ThreatView): void {
    t.body.destroy();
    t.marker.destroy();
    t.count.destroy();
    for (const r of t.rain) r.destroy();
  }

  update(v: View): void {
    const g = this.band;
    g.clear();
    const seen = new Set<string>();
    for (const t of v.state.threats) {
      seen.add(t.id);
      let tv = this.views.get(t.id);
      if (!tv) {
        tv = this.create(t, v);
        this.views.set(t.id, tv);
      }
      if (t.kind === 'storm') this.storm(t, tv, v, g);
      else if (t.kind === 'duo') this.duo(t, tv, v, g);
      else if (isLongThreat(t.kind)) drawLongThreat(t, tv, v, g);
      else if (isCard(t.kind)) drawCardThreat(t, tv, v, g);
      else this.critter(t, tv, v, g);
    }
    for (const [id, tv] of this.views) {
      if (seen.has(id)) continue;
      this.kill(tv);
      this.views.delete(id);
    }
  }

  private create(t: Threat, v: View): ThreatView {
    const tex =
      t.kind === 'smaa'
        ? `smaa_${t.variant ?? 'stork'}`
        : (m2Texture(t.kind) ?? m5Texture(t.kind) ?? (t.kind === 'storm' || t.kind === 'duo' ? 'cloudWarn' : t.kind === 'digger' ? 'digger' : 'anchor'));
    const body = v.atlas.sprite(tex);
    const marker = v.atlas.sprite('warning');
    const count = new BitmapText({ text: '', style: { fontFamily: NUM_FONT, fontSize: 22 } });
    count.anchor.set(0.5);
    const rain: Sprite[] = [];
    if (t.kind === 'storm') {
      for (let i = 0; i < v.quality.rain; i++) {
        const r = v.atlas.sprite('rain');
        r.visible = false;
        rain.push(r);
        this.c.addChild(r);
      }
    }
    this.c.addChild(body, marker, count);
    return { id: t.id, body, marker, count, rain, at: { x: 0, y: 0 }, flee: 0, fleeFrom: null, seed: hash(t.id), shooed: false, ang: null, popAt: -1e9, perchT: null, kind: t.kind, nudge: { x: 0, y: 0 }, target: { x: 0, y: 0 }, fade: 0 };
  }

  /** Stormens bane: fra venstre kant (varsel) til nedslaget og videre ud til højre. */
  private stormPath(t: Threat, v: View): { x0: number; xHit: number; x1: number; y: number } {
    const b = v.bounds;
    let sx = 0;
    let n = 0;
    for (const id of t.atRisk.length > 0 ? t.atRisk : t.hits) {
      const l = v.state.lines.find((x) => x.id === id);
      if (!l) continue;
      sx += lineMid(v.state, l).x;
      n++;
    }
    const xHit = n > 0 ? sx / n : b.x + b.w / 2;
    return { x0: b.x - 10, xHit, x1: b.x + b.w + 30, y: t.trackY };
  }

  private storm(t: Threat, tv: ThreatView, v: View, g: Graphics): void {
    const k = v.k;
    const clock = v.clock;
    const path = this.stormPath(t, v);
    let x: number;
    if (clock <= t.hitAt) {
      const f = Math.max(0, Math.min(1, (clock - t.warnAt) / Math.max(1, t.hitAt - t.warnAt)));
      x = path.x0 + (path.xHit - path.x0) * f;
    } else {
      const f = Math.max(0, Math.min(1, (clock - t.hitAt) / Math.max(1, t.endAt - t.hitAt)));
      x = path.xHit + (path.x1 - path.xHit) * f * f;
    }
    // Båndet: stormens spor over kortet.
    const left = v.cam.toScreen({ x: v.bounds.x - 400, y: t.trackY - t.trackHalf });
    const right = v.cam.toScreen({ x: v.bounds.x + v.bounds.w + 400, y: t.trackY + t.trackHalf });
    const y0 = left.y;
    const y1 = Math.max(right.y, y0 + 18 * k);
    const warn = t.phase === 'warning';
    const pulse = v.calm ? 0.5 : 0.5 + 0.5 * Math.sin(v.now / 260);
    g.rect(left.x, y0, right.x - left.x, y1 - y0).fill({ color: C.orange, alpha: warn ? 0.1 + pulse * 0.06 : 0.07 });
    // Skrå streger, så båndet ikke kun er en farve.
    const step = 22 * k;
    const shift = v.calm ? 0 : (v.now / 30) % step;
    for (let sx = left.x - (y1 - y0) + shift; sx < right.x; sx += step) {
      g.moveTo(sx, y1).lineTo(sx + (y1 - y0), y0);
    }
    g.stroke({ width: 2 * k, color: C.orange, alpha: warn ? 0.22 : 0.12 });
    for (const yy of [y0, y1]) {
      for (let sx = left.x; sx < right.x; sx += 16 * k) g.moveTo(sx, yy).lineTo(sx + 9 * k, yy);
    }
    g.stroke({ width: 2 * k, color: C.orange, alpha: 0.75 });

    const sp = v.cam.toScreen({ x, y: t.trackY });
    const warn0 = t.phase === 'warning';
    // Under varslet står skyen altid helt inde på skærmen: den startede ude i havet og blev skåret af.
    if (warn0) {
      const half = 62 * k * (v.mobile ? 0.85 : 1);
      sp.x = Math.max(half + 8, Math.min(v.width - half - 8, sp.x));
    }
    const bob = v.calm ? 0 : Math.sin(v.now / 300 + tv.seed) * 3 * k;
    const angry = !warn || t.hitAt - clock < 30;
    tv.body.texture = v.atlas.tex(angry ? 'cloudAngry' : 'cloudWarn');
    const ks = k * (v.mobile ? 0.85 : 1);
    tv.body.position.set(sp.x, sp.y - 28 * ks + bob);
    tv.body.scale.set(ks * (1 + (v.calm ? 0 : Math.sin(v.now / 400) * 0.03)), ks);
    // Halvgennemsigtig (60 %), så nettet og bynavnene under skyen stadig kan ses.
    tv.body.alpha = 0.6;
    tv.at = { x: sp.x, y: sp.y - 28 * ks };
    // Varselsmarkøren og nedtællingen rider på skyen. Ved nedslagsstedet dækkede de bynavne og
    // ringmenuen; sporet og de orange skjolde viser allerede, hvor stormen rammer.
    const secs = Math.max(0, Math.ceil((t.hitAt - clock) / TICK_HZ));
    tv.marker.visible = warn;
    tv.count.visible = warn;
    if (warn) {
      const my = Math.max(28 * k, sp.y - 84 * ks + bob);
      const mx = Math.max(24 * k, Math.min(v.width - 70 * k, sp.x));
      const s = k * (1 + (v.calm ? 0 : 0.06 * Math.sin(v.now / 160)));
      tv.marker.position.set(mx, my);
      tv.marker.scale.set(s);
      tv.count.text = `${secs}s`;
      tv.count.position.set(mx + 34 * k, my);
      tv.count.scale.set(k * 0.9);
      tv.count.tint = C.white;
    }
    // Regn, når stormen er over kortet.
    const raining = !warn && clock < t.endAt;
    const quota = raining ? tv.rain.length : Math.round(tv.rain.length * 0.25);
    for (let i = 0; i < tv.rain.length; i++) {
      const r = tv.rain[i] as Sprite;
      if (i >= quota || v.calm) {
        r.visible = false;
        continue;
      }
      r.visible = true;
      const ph = ((v.now / 700 + i * 0.137) % 1 + 1) % 1;
      const ox = ((i * 37) % 90) - 45;
      r.position.set(sp.x + (ox - ph * 10) * ks, sp.y - 10 * ks + ph * 60 * ks);
      r.scale.set(ks);
      r.alpha = Math.sin(ph * Math.PI) * 0.9;
    }
  }

  /**
   * Den fælles alarm: en lynsky over ledningen, en stiplet sigtelinje ned til den og to prikker, én
   * pr. rolle (turkis, når rollen har gjort sin del). Ved nedslaget et gult lyn, hvis det ramte.
   */
  private duo(t: Threat, tv: ThreatView, v: View, g: Graphics): void {
    const k = v.k;
    const clock = v.clock;
    const geo = v.lines.get(t.targetLine);
    if (!geo) {
      tv.body.visible = tv.marker.visible = tv.count.visible = false;
      return;
    }
    const mid = bez(geo.a, geo.c, geo.b, 0.5);
    const warn = t.phase === 'warning';
    const f = Math.max(0, Math.min(1, (clock - t.warnAt) / Math.max(1, t.hitAt - t.warnAt)));
    const ks = k * (v.mobile ? 0.6 : 0.7);
    const bob = v.calm ? 0 : Math.sin(v.now / 300 + tv.seed) * 3 * k;
    const y = Math.max(40 * k, mid.y - 74 * k) + bob;
    tv.body.visible = true;
    tv.body.texture = v.atlas.tex(!warn || f > 0.7 ? 'cloudAngry' : 'cloudWarn');
    tv.body.position.set(mid.x, y);
    tv.body.scale.set(ks);
    tv.body.alpha = warn ? Math.min(1, f * 6) * 0.85 : Math.max(0, Math.min(1, (t.endAt - clock) / 10)) * 0.85;
    tv.at = { x: mid.x, y };
    tv.marker.visible = warn;
    tv.count.visible = warn;
    if (warn) {
      const ph = v.calm ? 0.4 : (v.now / 900) % 1;
      g.circle(mid.x, mid.y, (16 + ph * 14) * k).stroke({ width: 3 * k, color: C.orange, alpha: 0.8 * (1 - ph) });
      const top = y + 24 * ks;
      const n = 6;
      for (let i = 0; i < n; i += 2) {
        const a = top + ((mid.y - top) * i) / n;
        const b = top + ((mid.y - top) * (i + 1)) / n;
        g.moveTo(mid.x, a).lineTo(mid.x, b);
      }
      g.stroke({ width: 2.5 * k, color: C.orange, alpha: 0.85 });
      const d = t.duo ?? { plan: false, drift: false };
      ([['drift', -1], ['plan', 1]] as const).forEach(([role, side]) => {
        const done = d[role];
        g.circle(mid.x + side * 14 * k, y - 34 * ks, 7 * k)
          .fill({ color: done ? C.turquoise : C.white, alpha: done ? 1 : 0.9 })
          .stroke({ width: 2 * k, color: done ? C.white : C.orange });
      });
      const secs = Math.max(0, Math.ceil((t.hitAt - clock) / TICK_HZ));
      tv.marker.position.set(mid.x + 46 * k, y - 18 * k);
      tv.marker.scale.set(k * 0.75);
      tv.count.text = `${secs}s`;
      tv.count.position.set(mid.x + 74 * k, y - 16 * k);
      tv.count.scale.set(k * 0.8);
      tv.count.tint = C.white;
    } else if (t.hits.length > 0 && clock - t.hitAt < 8 && !v.calm) {
      const top = y + 20 * ks;
      const zig = [0, 0.25, 0.45, 0.7, 1];
      g.moveTo(mid.x, top);
      zig.forEach((z, i) => g.lineTo(mid.x + (i % 2 === 0 ? -8 : 8) * k * (i === zig.length - 1 ? 0 : 1), top + (mid.y - top) * z));
      g.stroke({ width: 4 * k, color: C.yellow, alpha: 0.95 });
    }
  }

  private critter(t: Threat, tv: ThreatView, v: View, g: Graphics): void {
    const k = v.k;
    const clock = v.clock;
    const geo = v.lines.get(t.targetLine);
    const line = v.state.lines.find((l) => l.id === t.targetLine);
    if (!geo || !line) {
      tv.body.visible = tv.marker.visible = tv.count.visible = false;
      return;
    }
    // Gæster og gravere står på land. Playtest runde 3: en ko stod midt i Limfjorden, fordi
    // ledningens midte lå over vand. Ankerbåden står i vandet og bruger midten.
    if (tv.perchT === null) tv.perchT = t.kind === 'anchor' ? 0.5 : this.landT(geo, v);
    const mid = bez(geo.a, geo.c, geo.b, tv.perchT);
    if (tv.ang === null) tv.ang = t.kind === 'anchor' ? this.seaAngle(mid, tv.seed, v) : ((tv.seed % 360) / 180) * Math.PI;
    const ang = tv.ang;
    const far = { x: mid.x + Math.cos(ang) * 140 * k, y: mid.y + Math.sin(ang) * 90 * k };
    const f = Math.max(0, Math.min(1, (clock - t.warnAt) / Math.max(1, t.hitAt - t.warnAt)));
    let p: P;
    if (t.kind === 'smaa') {
      this.smaa(t, tv, v, g, mid, far);
      return;
    }
    if (t.shooed || tv.shooed) {
      if (!tv.fleeFrom) tv.fleeFrom = { ...tv.at };
      tv.flee = Math.min(1, tv.flee + v.dt / 900);
      const e = tv.flee * tv.flee;
      p = { x: tv.fleeFrom.x + (far.x - tv.fleeFrom.x) * e * 1.6, y: tv.fleeFrom.y + (far.y - tv.fleeFrom.y) * e * 1.6 };
      tv.body.alpha = 1 - tv.flee;
    } else if (t.phase === 'warning') {
      const e = 1 - (1 - f) * (1 - f);
      // Forskydningen gælder også under varslet: ellers flyttede scenen figuren ét billede ad gangen,
      // og den næste tegning stod igen uden, så figuren dansede (T24 ved Malling på bane 3).
      p = { x: far.x + (mid.x - far.x) * e + tv.nudge.x, y: far.y + (mid.y - far.y) * e + 14 * k + tv.nudge.y };
      tv.body.alpha = Math.min(1, f * 6);
    } else {
      p = { x: mid.x + tv.nudge.x, y: mid.y + 14 * k + tv.nudge.y };
      tv.body.alpha = Math.max(0, Math.min(1, (t.endAt - clock) / 10));
    }
    const wob = v.calm ? 0 : Math.sin(v.now / 120 + tv.seed) * (t.phase === 'warning' ? 1.5 : 0.5) * k;
    tv.body.visible = true;
    tv.body.position.set(p.x, p.y + wob);
    const dir = far.x > mid.x ? -1 : 1;
    tv.body.scale.set(k * 1.1 * (t.shooed || tv.shooed ? -dir : dir), k * 1.1);
    tv.at = { x: p.x, y: p.y - 12 * k };
    const warn = t.phase === 'warning' && !t.shooed && !tv.shooed;
    tv.marker.visible = warn;
    tv.count.visible = warn;
    if (warn) {
      // Tryk-ring om figuren: "tap mig væk".
      const ph = v.calm ? 0.4 : (v.now / 900) % 1;
      g.circle(tv.at.x, tv.at.y, (26 + ph * 14) * k).stroke({ width: 3 * k, color: C.orange, alpha: 0.8 * (1 - ph) });
      g.circle(tv.at.x, tv.at.y, 26 * k).fill({ color: C.white, alpha: 0.35 }).stroke({ width: 2.5 * k, color: C.orange });
      const secs = Math.max(0, Math.ceil((t.hitAt - clock) / TICK_HZ));
      // Trekant og nedtælling side om side over figuren: under den dækkede tallet bynavne.
      tv.marker.position.set(p.x + 22 * k, p.y - 46 * k);
      tv.marker.scale.set(k * 0.75);
      tv.count.text = `${secs}s`;
      tv.count.position.set(p.x + 50 * k, p.y - 44 * k);
      tv.count.scale.set(k * 0.8);
    }
  }

  /**
   * Småt-gæsten: flyver ind i varslet, sidder på tråden og vipper, og hopper væk i en bue, når den
   * jages (eller selv tager af sted). Fod på tråden, så den ser ud til at sidde på ledningen.
   */
  private smaa(t: Threat, tv: ThreatView, v: View, g: Graphics, mid: P, far: P): void {
    const k = v.k;
    const clock = v.clock;
    const gone = t.shooed || tv.shooed;
    const f = Math.max(0, Math.min(1, (clock - t.warnAt) / Math.max(1, t.hitAt - t.warnAt)));
    let p: P;
    let rot = 0;
    if (gone) {
      if (!tv.fleeFrom) tv.fleeFrom = { ...mid };
      tv.flee = Math.min(1, tv.flee + v.dt / 700);
      const e = tv.flee;
      // To små hop og så væk: en bue mod kanten.
      const hop = Math.abs(Math.sin(e * Math.PI * 2.5)) * 26 * k * (1 - e * 0.5);
      p = { x: tv.fleeFrom.x + (far.x - tv.fleeFrom.x) * e * 1.4, y: tv.fleeFrom.y + (far.y - tv.fleeFrom.y) * e * 1.4 - hop };
      tv.body.alpha = Math.min(1, 1.6 - tv.flee * 1.6);
      rot = v.calm ? 0 : (far.x > mid.x ? 1 : -1) * 0.25 * e;
    } else if (t.phase === 'warning') {
      // Lander: glider ind fra siden og sætter sig de sidste 30 %.
      const e = Math.min(1, f / 0.7);
      const ee = 1 - (1 - e) * (1 - e);
      const arc = Math.sin(e * Math.PI) * 30 * k;
      p = { x: far.x + (mid.x - far.x) * ee + tv.nudge.x, y: far.y + (mid.y - far.y) * ee - arc + tv.nudge.y };
      tv.body.alpha = Math.min(1, f * 6);
      rot = v.calm ? 0 : Math.sin(v.now / 200 + tv.seed) * 0.12;
    } else {
      // Sidder og vipper på tråden. Forsvinder blidt til sidst, når den selv tager af sted.
      p = { x: mid.x + tv.nudge.x, y: mid.y + tv.nudge.y };
      tv.body.alpha = Math.max(0, Math.min(1, (t.endAt - clock) / 10));
      rot = v.calm ? 0 : Math.sin(v.now / 380 + tv.seed) * 0.16;
    }
    const dir = far.x > mid.x ? -1 : 1;
    tv.body.visible = true;
    tv.body.position.set(p.x, p.y);
    tv.body.rotation = rot;
    tv.body.scale.set(k * 1.05 * (gone ? -dir : dir), k * 1.05);
    tv.at = { x: p.x, y: p.y - 16 * k };
    const warn = t.phase === 'warning' && !gone;
    tv.marker.visible = false;
    tv.count.visible = warn;
    if (!gone && (warn || t.phase === 'active')) {
      // Tryk-ring: man kan jage gæsten væk, så længe den sidder der. Hvid og rolig, ikke alarm.
      const ph = v.calm ? 0.4 : (v.now / 1100) % 1;
      g.circle(tv.at.x, tv.at.y, (24 + ph * 10) * k).stroke({ width: 2.5 * k, color: warn ? C.orange : C.white, alpha: 0.8 * (1 - ph) });
      g.circle(tv.at.x, tv.at.y, 24 * k).fill({ color: C.white, alpha: 0.3 });
    }
    if (warn) {
      const secs = Math.max(0, Math.ceil((t.hitAt - clock) / TICK_HZ));
      tv.count.text = `${secs}s`;
      tv.count.position.set(p.x + 30 * k, p.y - 40 * k);
      tv.count.scale.set(k * 0.8);
    }
  }

  /** Første punkt på ledningen, der ligger på land, fra midten og ud mod enderne (0.5 hvis intet). */
  private landT(geo: { a: P; b: P; c: P }, v: View): number {
    for (const tt of [0.5, 0.4, 0.6, 0.3, 0.7, 0.2, 0.8, 0.12, 0.88]) {
      if (v.onLand(v.cam.toWorld(bez(geo.a, geo.c, geo.b, tt)))) return tt;
    }
    return 0.5;
  }

  /** Den retning fra kablets midte, hvor der er mest vand: ankerbåden sejler aldrig over land. */
  private seaAngle(mid: P, seed: number, v: View): number {
    const base = ((seed % 360) / 180) * Math.PI;
    let best = base;
    let bestWet = -1;
    for (let i = 0; i < 16; i++) {
      const a = base + (i * Math.PI) / 8;
      let wet = 0;
      for (const d of [0.25, 0.5, 0.75, 1, 1.4]) {
        const s = { x: mid.x + Math.cos(a) * 140 * v.k * d, y: mid.y + Math.sin(a) * 90 * v.k * d };
        const w = v.cam.toWorld(s);
        if (!v.onLand(w)) wet++;
      }
      if (wet > bestWet) {
        bestWet = wet;
        best = a;
      }
      if (wet === 5) break;
    }
    return best;
  }

  /** Tappet væk: vis flugten med det samme, også før sim'en har kørt sit næste tick. */
  /** Kortet er besvaret: figuren hopper. */
  pop(id: string): void {
    const tv = this.views.get(id);
    if (tv) tv.popAt = performance.now();
  }

  markShooed(id: string): void {
    const tv = this.views.get(id);
    if (tv) tv.shooed = true;
  }

  /** Trussel under fingeren (gravko, ankerbåd eller et ubesvaret kort i varslet). Mindst 52 px, også på telefon: truslen vinder over noder og ledninger. */
  hit(p: P, v: View): string | null {
    let best: string | null = null;
    let bestD = Math.max(52, 46 * v.k);
    for (const t of v.state.threats) {
      // Småt-gæster kan jages, så længe de sidder der; de andre kun i varslet.
      // Den fælles alarm klares med rollernes egne knapper, ikke ved at trykke på skyen.
      if (t.kind === 'storm' || t.kind === 'duo' || isLongThreat(t.kind) || t.shooed) continue;
      if (t.phase !== 'warning' && !(t.kind === 'smaa' && t.phase === 'active')) continue;
      if (t.card && !cardOpen(t)) continue;
      const tv = this.views.get(t.id);
      if (!tv || tv.shooed) continue;
      const d = Math.hypot(tv.at.x - p.x, tv.at.y - p.y);
      if (d < bestD) {
        bestD = d;
        best = t.id;
      }
    }
    return best;
  }

  /**
   * En trussel, der ikke kan jages (storm, vindstille, fælles lyn og de lange), tæt på punktet. Kun
   * ikonet, ikke stormbåndet: et tryk på en ledning i stormen skal stadig ramme ledningen.
   */
  near(p: P, v: View): string | null {
    let best: string | null = null;
    let bestD = Math.max(36, 32 * v.k);
    for (const t of v.state.threats) {
      if (t.shooed || t.kind === 'smaa' || t.card) continue;
      const tv = this.views.get(t.id);
      if (!tv || tv.shooed) continue;
      const d = Math.hypot(tv.at.x - p.x, tv.at.y - p.y);
      if (d < bestD) {
        bestD = d;
        best = t.id;
      }
    }
    return best;
  }

  screenOf(id: string): P | null {
    const tv = this.views.get(id);
    return tv ? { ...tv.at } : null;
  }
}
